// Order Service - Hybrid (Remote-first with Local Mock Fallback)
// Public API stays stable for UI. Tries HTTP endpoints first and
// gracefully falls back to localStorage-based mock data.

import { generateMockOrders } from '../mocks/orderMockData';
import orderBusinessLogic from '../utils/orderBusinessLogic';
import remote from './orderRemoteService';

const { 
  calculateOrderStatus, 
  getNextProductionStatus,
  isValidProductionTransition
} = orderBusinessLogic;

const STORAGE_KEY = 'mock.orders.v1';

// Status enums
export const ORDER_STATUSES = [
  'AGUARDANDO PAGAMENTO',
  'EM ANDAMENTO',
  'PRONTO PARA COLETA',
  'ENVIADO',
  'CONCLUÍDO',
  'PARCIALMENTE DEVOLVIDO',
  'DEVOLVIDO',
  'CANCELADO'
];

export const PRODUCTION_STATUSES = ['PENDENTE', 'PREPARANDO', 'PRONTO'];

// Internal helpers
function readAll() {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) {
    return seedOrders();
  }
  try {
    const orders = JSON.parse(data);
    // Ensure all orders have calculated status
    return orders.map(order => ({
      ...order,
      status: order.status || calculateOrderStatus(order)
    }));
  } catch {
    return [];
  }
}

function seedOrders() {
  const orders = generateMockOrders();
  // Calculate status for each order based on business logic
  const seeded = orders.map(order => ({
    ...order,
    status: order.status || calculateOrderStatus(order)
  }));
  writeAll(seeded);
  return seeded;
}

function writeAll(orders) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
}

function delay(data, ms = 150) {
  return new Promise(resolve => setTimeout(() => resolve(data), ms));
}

// Simplified order for list view
function simplifyOrder(order) {
  return {
    id: order.id,
    code: order.code,
    createdAt: order.createdAt,
    status: order.status,
    total: order.pricing.total,
    productCount: order.products.length,
    productThumbs: order.products.slice(0, 5).map(p => ({ 
      id: p.id, 
      name: p.name,
      thumbnail: p.thumbnail 
    }))
  };
}

// Public API
export async function fetchOrders({ statuses = [], search = '' } = {}) {
  // Try remote first
  try {
    const remoteList = await remote.listOrders({ statuses, search });
    return remoteList;
  } catch {
    // Fallback to local mock
    const list = readAll();
    let filtered = list;
    if (statuses.length) {
      const set = new Set(statuses);
      filtered = filtered.filter(o => set.has(o.status));
    }
    const term = typeof search === 'string' ? search.trim().toLowerCase() : '';
    if (term) {
      filtered = filtered.filter(o => o.code.toLowerCase().includes(term));
    }
    return delay(filtered.map(simplifyOrder));
  }
}

export async function fetchProductionItems({ statuses = [] } = {}) {
  // Try remote list, then flatten locally
  try {
    const list = await fetchOrders();
    // Remote listOrders returns simplified orders; fetch details for items
    const detailed = await Promise.all(list.map(async o => {
      try {
        const full = await fetchOrderDetails(o.id);
        return full;
      } catch {
        return null;
      }
    }));
    const items = detailed.filter(Boolean).flatMap(o => (o.products || []).map(p => ({
      ...p,
      orderId: o.id,
      orderCode: o.code,
    })));
    let filtered = items;
    if (statuses.length) {
      const set = new Set(statuses);
      filtered = filtered.filter(i => set.has(i.productionStatus));
    }
    return filtered;
  } catch {
    const list = readAll();
    const items = list.flatMap(o => o.products.map(p => ({
      ...p,
      orderId: o.id,
      orderCode: o.code
    })));
    let filtered = items;
    if (statuses.length) {
      const set = new Set(statuses);
      filtered = filtered.filter(i => set.has(i.productionStatus));
    }
    return delay(filtered);
  }
}

export async function fetchOrderDetails(orderId) {
  try {
    const order = await remote.getOrder(orderId);
    return order;
  } catch {
    const list = readAll();
    const order = list.find(o => o.id === orderId);
    return delay(order || null);
  }
}

export async function updateProductionItemStatus(orderId, itemId, newStatus) {
  // Try remote (uses itemId as merchandise SKU as we map id <- SKU)
  try {
    if (newStatus && !['PENDENTE','PREPARANDO','PRONTO','ENTREGUE','CANCELADO','DEVOLVIDO'].includes(newStatus)) {
      throw new Error('invalid status');
    }
    const desired = newStatus;
    // If no explicit target, compute next using business logic by reading current
    let statusToSend = desired;
    if (!statusToSend) {
      const current = await fetchOrderDetails(orderId);
      const item = current?.products?.find(p => p.id === itemId);
      if (!item) throw new Error('item not found');
      statusToSend = getNextProductionStatus(item.productionStatus);
    }
    await remote.updateItemStatus(orderId, itemId, statusToSend);
    return true;
  } catch {
    // Fallback to local
    const list = readAll();
    const order = list.find(o => o.id === orderId);
    if (!order) return delay(false);
    const item = order.products.find(p => p.id === itemId);
    if (!item) return delay(false);
    if (newStatus && !isValidProductionTransition(item.productionStatus, newStatus)) {
      return delay(false);
    }
    item.productionStatus = newStatus || getNextProductionStatus(item.productionStatus);
    order.status = calculateOrderStatus(order);
    writeAll(list);
    return delay(true);
  }
}

export async function updateOrderStatus(orderId, newStatus) {
  // Remote attempt is not documented for direct order status update; keep local
  const list = readAll();
  const idx = list.findIndex(o => o.id === orderId);
  if (idx === -1) return delay(false);
  list[idx].status = newStatus;
  writeAll(list);
  return delay(true);
}

export async function cancelOrder(orderId) {
  try {
    await remote.cancelOrder(orderId);
    return true;
  } catch {
    return updateOrderStatus(orderId, 'CANCELADO');
  }
}

/**
 * Debug utility: Reset orders to initial mock state
 * Clears localStorage and regenerates seed data
 */
export async function resetOrdersToMock() {
  localStorage.removeItem(STORAGE_KEY);
  const fresh = seedOrders();
  return delay(fresh);
}

// ============================================================================
// API Integration Placeholders
// ============================================================================
// The following functions are placeholders for future backend integration.
// Replace mock localStorage logic with actual API calls when backend is ready.

/**
 * Fetch order returns/refunds
 * @param {string} orderId - Order ID
 * @returns {Promise<Array>} Array of return entries
 * 
 * Expected API response format:
 * [
 *   {
 *     id: 'RET-123',
 *     createdAt: '2024-01-15T10:30:00Z',
 *     amount: 45.80,
 *     reason: 'Produto defeituoso',
 *     items: [{ productId: 'p1', quantity: 2, name: 'Product', variant: 'Variant' }],
 *     receiptUrl: 'https://api.example.com/receipts/ret123.pdf'
 *   }
 * ]
 */
export async function fetchOrderReturns(orderId) {
  // TODO: Replace with API call
  // const response = await fetch(`/api/orders/${orderId}/returns`);
  // return response.json();
  const order = await fetchOrderDetails(orderId);
  return delay(order?.returns || []);
}

/**
 * Create a new return for an order
 * @param {string} orderId - Order ID
 * @param {Object} returnData - Return information
 * @returns {Promise<Object>} Created return entry
 * 
 * Expected request format:
 * {
 *   amount: 45.80,
 *   reason: 'Produto defeituoso',
 *   items: [{ productId: 'p1', quantity: 2 }],
 *   receiptFile: File (optional)
 * }
 */
export async function createReturn(orderId, returnData) {
  // TODO: Replace with API call
  // const formData = new FormData();
  // formData.append('amount', returnData.amount);
  // formData.append('reason', returnData.reason);
  // formData.append('items', JSON.stringify(returnData.items));
  // if (returnData.receiptFile) formData.append('receipt', returnData.receiptFile);
  // const response = await fetch(`/api/orders/${orderId}/returns`, {
  //   method: 'POST',
  //   body: formData
  // });
  // return response.json();
  
  console.warn('createReturn: API integration pending');
  return delay({ success: false, message: 'API not implemented' });
}

/**
 * Fetch payment receipt
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Receipt information
 * 
 * Expected API response format:
 * {
 *   url: 'https://api.example.com/receipts/payment123.pdf',
 *   filename: 'comprovante_pagamento_51220.pdf',
 *   mimeType: 'application/pdf'
 * }
 */
export async function fetchPaymentReceipt(orderId) {
  // TODO: Replace with API call
  // const response = await fetch(`/api/orders/${orderId}/payment/receipt`);
  // return response.json();
  const order = await fetchOrderDetails(orderId);
  return delay({
    url: order?.payment?.receiptUrl || null,
    filename: `comprovante_${orderId}.pdf`,
    mimeType: 'application/pdf'
  });
}

/**
 * Validate order completion with code
 * @param {string} orderId - Order ID
 * @param {string} code - Validation code
 * @returns {Promise<Object>} Validation result
 * 
 * Expected request format:
 * { code: '123456' }
 * 
 * Expected response:
 * { success: true, order: {...} } or { success: false, error: 'Invalid code' }
 */
export async function validateOrderCompletion(orderId, code) {
  // TODO: Replace with API call
  // const response = await fetch(`/api/orders/${orderId}/complete`, {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify({ code })
  // });
  // return response.json();
  
  console.warn('validateOrderCompletion: API integration pending');
  return delay({ success: false, message: 'API not implemented' });
}

/**
 * Validate return with code
 * @param {string} orderId - Order ID
 * @param {string} returnId - Return ID
 * @param {string} code - Validation code
 * @returns {Promise<Object>} Validation result
 */
export async function validateReturn(orderId, returnId, code) {
  // TODO: Replace with API call
  // const response = await fetch(`/api/orders/${orderId}/returns/${returnId}/validate`, {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify({ code })
  // });
  // return response.json();
  
  console.warn('validateReturn: API integration pending');
  return delay({ success: false, message: 'API not implemented' });
}

const orderService = {
  fetchOrders,
  fetchProductionItems,
  fetchOrderDetails,
  updateProductionItemStatus,
  updateOrderStatus,
  cancelOrder,
  resetOrdersToMock,
  // API placeholders
  fetchOrderReturns,
  createReturn,
  fetchPaymentReceipt,
  validateOrderCompletion,
  validateReturn,
  // Constants
  ORDER_STATUSES,
  PRODUCTION_STATUSES
};

export default orderService;
